import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  MONGODB_AWSGW_QUERIES,
  MONGODB_AWSGW_APIKEY,
  MONGODB_DATASOURCE_TS,
} from "../../utility/keys";
import CompassResultsTable from "./CompassResultsTable";
import { buildMongoQuickFilter } from "../../utility/compassMongoFilter";

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// Compass (#191) — MongoDB backend browser.
//
// Fetches a page from the query Lambda's `browseCollection` method (true
// skip/limit + countDocuments). This is the path that delivers the
// Compass-killer pagination: jump to any page, jump to last, adjustable page
// size — the exact thing MongoDB Compass won't do. READ ONLY.
//
// `source` → database: uat reads the "UAT" db, prod reads the "Warehouses" db.
// The single query Lambda can read either database on the shared cluster, so
// Compass browses BOTH environments regardless of which env served the app.
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

const SOURCE_TO_DB = { uat: "UAT", prod: "Warehouses" };

function CompassMongoBrowser({
  source,
  mongoCollection,
  rawFilter,
  searchField,
  searchValue,
  pageSize,
  previewFields,
  selectedId,
  onSelect,
}) {
  const [page, setPage] = useState(1);
  const [pageInput, setPageInput] = useState("1");

  const database = SOURCE_TO_DB[source] || "UAT";
  const filter = useMemo(
    () => buildMongoQuickFilter(rawFilter, searchField, searchValue),
    [rawFilter, searchField, searchValue]
  );

  // Reset to page 1 whenever the query inputs change.
  useEffect(() => {
    setPage(1);
    setPageInput("1");
  }, [source, mongoCollection, searchField, searchValue, pageSize, rawFilter]);

  const { data, isFetching, error } = useQuery({
    queryKey: [
      "compassMongoBrowse",
      database,
      mongoCollection,
      JSON.stringify(filter),
      page,
      pageSize,
    ],
    queryFn: async () => {
      const body = {
        method: "browseCollection",
        dataSource: MONGODB_DATASOURCE_TS,
        database,
        collection: mongoCollection,
        filter,
        sort: { _id: 1 },
        page,
        pageSize,
      };
      const response = await fetch(MONGODB_AWSGW_QUERIES, {
        method: "POST",
        headers: {
          "x-api-key": MONGODB_AWSGW_APIKEY,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });
      const json = await response.json();
      return json?.message?.result ?? null;
    },
    keepPreviousData: true,
  });

  const docs = useMemo(() => data?.result ?? [], [data]);
  const totalCount = data?.totalCount ?? 0;
  const totalPages = data?.totalPages ?? 1;

  // Never leave the document pane empty when rows exist: auto-select the first
  // row whenever the displayed set changes (collection/source/filter/search/
  // page) and the current selection isn't in it. Manual clicks on a row that's
  // still present are preserved.
  useEffect(() => {
    if (!docs.length || !onSelect) return;
    const stillShown = selectedId != null && docs.some((d) => d._id === selectedId);
    if (!stillShown) onSelect(docs[0]);
  }, [docs, selectedId, onSelect]);

  const go = (p) => {
    const clamped = Math.min(Math.max(1, p), totalPages);
    setPage(clamped);
    setPageInput(String(clamped));
  };

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex-1 min-h-0">
        <CompassResultsTable
          docs={docs}
          previewFields={previewFields}
          loading={isFetching && docs.length === 0}
          error={error ? String(error.message || error) : null}
          onSelect={onSelect}
          selectedId={selectedId}
        />
      </div>

      {/* Offset pagination — the Compass-killer controls */}
      <div className="flex items-center gap-2 pt-2 text-xs flex-wrap">
        <button className="px-2 py-1 border-2 border-skin-base rounded-sm disabled:opacity-40" onClick={() => go(1)} disabled={page <= 1}>
          |◀ First
        </button>
        <button className="px-2 py-1 border-2 border-skin-base rounded-sm disabled:opacity-40" onClick={() => go(page - 1)} disabled={page <= 1}>
          ◀ Prev
        </button>
        <span className="px-1">
          Page
          <input
            value={pageInput}
            onChange={(e) => setPageInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                const n = parseInt(pageInput, 10);
                if (Number.isFinite(n)) go(n);
              }
            }}
            className="w-14 mx-1 px-1 py-0.5 text-black rounded-sm text-center"
            aria-label="Jump to page"
          />
          of {totalPages.toLocaleString()}
        </span>
        <button className="px-2 py-1 border-2 border-skin-base rounded-sm disabled:opacity-40" onClick={() => go(page + 1)} disabled={page >= totalPages}>
          Next ▶
        </button>
        <button className="px-2 py-1 border-2 border-skin-base rounded-sm disabled:opacity-40" onClick={() => go(totalPages)} disabled={page >= totalPages}>
          Last ▶|
        </button>
        <span className="ml-auto text-skin-link">
          {totalCount.toLocaleString()} document{totalCount === 1 ? "" : "s"}
          {isFetching ? " · loading…" : ""}
        </span>
      </div>
    </div>
  );
}

export default CompassMongoBrowser;
